import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  DatePicker,
  Descriptions,
  Drawer,
  Flex,
  Input,
  Modal,
  Segmented,
  Select,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import Table from 'components/Common/Table';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import WhatsAppLayout from "components/WhatsApp/WhatsAppLayout";
import { useNotification } from "NotificationContext";
import * as api from "api/activityTemplates";

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/**
 * Everything sent and received through one WhatsApp number or one email
 * configuration: the inbox traffic plus activity sends, including the ones
 * that failed before Meta / the SMTP server accepted them.
 */

const STATUS_COLORS = {
  QUEUED: "default",
  SENT: "blue",
  DELIVERED: "cyan",
  READ: "green",
  RECEIVED: "purple",
  FAILED: "red",
  BOUNCED: "red",
};

const STATUS_OPTIONS = {
  whatsapp: ["QUEUED", "SENT", "DELIVERED", "READ", "FAILED", "RECEIVED"],
  email: ["QUEUED", "SENT", "FAILED", "BOUNCED", "RECEIVED"],
};

const PAGE_SIZE = 20;

const formatTime = (value) => (value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "—");

const statusTag = (row) => (
  <Space size={4}>
    <Tag color={STATUS_COLORS[row.status] || "default"} style={{ marginInlineEnd: 0 }}>
      {row.status}
    </Tag>
    {row.error_message && (
      <Tooltip title={row.error_message}>
        <ExclamationCircleOutlined style={{ color: "#cf1322" }} />
      </Tooltip>
    )}
  </Space>
);

const directionTag = (direction) =>
  direction === "inbound" ? (
    <Tag icon={<ArrowDownOutlined />} color="purple">
      In
    </Tag>
  ) : (
    <Tag icon={<ArrowUpOutlined />} color="geekblue">
      Out
    </Tag>
  );

/** The WhatsApp bubble for a log row, stamped with when it was logged. */
const WhatsAppBubble = ({ row }) => {
  const layout =
    row.layout || (row.body ? { header_type: "NONE", body: row.body } : null);
  return (
    <div style={{ background: "#efeae2", borderRadius: 8, padding: 12 }}>
      <div
        style={{
          background: row.direction === "inbound" ? "#fff" : "#d9fdd3",
          borderRadius: 8,
          padding: "6px 8px 4px",
          maxWidth: 360,
          marginLeft: row.direction === "inbound" ? 0 : "auto",
          boxShadow: "0 1px 0.5px rgba(11,20,26,0.13)",
        }}
      >
        {layout ? (
          <WhatsAppLayout layout={layout}>
            <div style={{ textAlign: "right", fontSize: 10.5, color: "#8696a0", marginTop: 2 }}>
              {dayjs(row.timestamp).format("HH:mm")}
            </div>
          </WhatsAppLayout>
        ) : (
          <Text type="secondary">[{row.type}]</Text>
        )}
      </div>
    </div>
  );
};

const LogDetail = ({ row, channel, onClose }) => (
  <Modal
    title={row ? `${row.direction === "inbound" ? "Received from" : "Sent to"} ${row.counterparty}` : ""}
    open={!!row}
    onCancel={onClose}
    footer={null}
    width={720}
    destroyOnClose
  >
    {row && (
      <>
        <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }}>
          <Descriptions.Item label="Status">{statusTag(row)}</Descriptions.Item>
          <Descriptions.Item label="Direction">{directionTag(row.direction)}</Descriptions.Item>
          <Descriptions.Item label="From">{row.from_address || "—"}</Descriptions.Item>
          <Descriptions.Item label="To">{row.to_address || "—"}</Descriptions.Item>
          {row.counterparty_name && (
            <Descriptions.Item label="Contact" span={2}>
              {row.counterparty_name}
            </Descriptions.Item>
          )}
          {row.template_name && (
            <Descriptions.Item label="Template" span={2}>
              {row.template_name}
            </Descriptions.Item>
          )}
          {row.subject && (
            <Descriptions.Item label="Subject" span={2}>
              {row.subject}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Logged">{formatTime(row.timestamp)}</Descriptions.Item>
          <Descriptions.Item label="Sent">{formatTime(row.sent_at)}</Descriptions.Item>
          {channel === "whatsapp" && (
            <>
              <Descriptions.Item label="Delivered">{formatTime(row.delivered_at)}</Descriptions.Item>
              <Descriptions.Item label="Read">{formatTime(row.read_at)}</Descriptions.Item>
            </>
          )}
          {row.error_message && (
            <Descriptions.Item label="Error" span={2}>
              <Text type="danger">{row.error_message}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
        {channel === "whatsapp" ? (
          <WhatsAppBubble row={row} />
        ) : row.body_format === "html" ? (
          // Our own rendered template; sandboxed so its styles stay inside.
          <iframe
            title="Email body"
            sandbox=""
            srcDoc={row.body || ""}
            style={{ width: "100%", height: "50vh", border: "1px solid #f0f0f0", borderRadius: 4 }}
          />
        ) : (
          <Paragraph
            style={{
              whiteSpace: "pre-wrap",
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 4,
              padding: 12,
              maxHeight: "50vh",
              overflow: "auto",
            }}
          >
            {row.body || <Text type="secondary">No body</Text>}
          </Paragraph>
        )}
      </>
    )}
  </Modal>
);

/**
 * `channel` is "whatsapp" or "email"; `configuration` the row whose logs to
 * show. Render it with `key={configuration?.id}` so another row starts fresh.
 */
const MessageLogsDrawer = ({ channel, configuration, onClose }) => {
  const notify = useNotification();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [direction, setDirection] = useState("");
  const [status, setStatus] = useState();
  const [search, setSearch] = useState("");
  const [range, setRange] = useState(null);
  const [selected, setSelected] = useState(null);
  const isWhatsapp = channel === "whatsapp";
  const id = configuration?.id;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const params = {
      page,
      page_size: pageSize,
      direction,
      status,
      q: search,
      date_from: range?.[0]?.format("YYYY-MM-DD"),
      date_to: range?.[1]?.format("YYYY-MM-DD"),
    };
    try {
      const response = isWhatsapp
        ? await api.loadWhatsappConfigurationLogs(id, params)
        : await api.loadEmailConfigurationLogs(id, params);
      setRows(response.data?.results || []);
      setTotal(response.data?.count || 0);
      setSummary(response.data?.summary || null);
    } catch (err) {
      notify("error", "Could not load logs", err?.response?.data?.detail || err?.message);
    }
    setLoading(false);
  }, [id, isWhatsapp, page, pageSize, direction, status, search, range, notify]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change goes back to the first page.
  const filtered = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const columns = [
    {
      title: "Time",
      dataIndex: "timestamp",
      width: 170,
      render: formatTime,
    },
    {
      title: "",
      dataIndex: "direction",
      width: 70,
      render: directionTag,
    },
    {
      title: isWhatsapp ? "Contact" : "Counterparty",
      dataIndex: "counterparty",
      width: 200,
      render: (value, row) => (
        <div style={{ lineHeight: 1.3 }}>
          <div>{isWhatsapp && value ? `+${value}` : value || "—"}</div>
          {row.counterparty_name && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {row.counterparty_name}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Message",
      dataIndex: "preview",
      ellipsis: true,
      render: (value, row) => (
        <Space size={4} style={{ maxWidth: "100%" }}>
          {row.template_name && <Tag color="blue">{row.template_name}</Tag>}
          {!isWhatsapp && row.subject && <Text strong>{row.subject}</Text>}
          <Text type="secondary" ellipsis>
            {isWhatsapp || !row.subject ? value : `— ${value}`}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 130,
      render: (_, row) => statusTag(row),
    },
  ];

  const number = configuration?.number?.phone_number;
  const title = isWhatsapp
    ? `Logs · ${configuration?.name || ""}${number ? ` (+${number})` : ""}`
    : `Logs · ${configuration?.name || ""}${configuration?.from_email ? ` (${configuration.from_email})` : ""}`;

  return (
    <Drawer
      title={title}
      open={!!configuration}
      onClose={onClose}
      width="min(1100px, 100vw)"
      destroyOnClose
      extra={
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      }
    >
      {summary && (
        <Flex gap={32} wrap style={{ marginBottom: 16 }}>
          <Statistic title="Total" value={summary.total} />
          <Statistic title="Sent" value={summary.outbound} />
          <Statistic title="Received" value={summary.inbound} />
          <Statistic
            title="Failed"
            value={summary.failed}
            valueStyle={summary.failed ? { color: "#cf1322" } : undefined}
          />
        </Flex>
      )}
      <Flex gap="small" wrap style={{ marginBottom: 12 }}>
        <Segmented
          value={direction}
          onChange={filtered(setDirection)}
          options={[
            { label: "All", value: "" },
            { label: "Sent", value: "outbound" },
            { label: "Received", value: "inbound" },
          ]}
        />
        <Select
          allowClear
          placeholder="Any status"
          style={{ width: 150 }}
          value={status}
          onChange={filtered(setStatus)}
          options={STATUS_OPTIONS[channel].map((value) => ({ value, label: value }))}
        />
        <RangePicker value={range} onChange={filtered(setRange)} />
        <Input.Search
          allowClear
          placeholder={isWhatsapp ? "Number, name or text" : "Address, name or subject"}
          style={{ width: 260 }}
          onSearch={filtered(setSearch)}
        />
      </Flex>
      <Table
        rowKey={(row) => `${row.source}-${row.id}`}
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        onRow={(row) => ({ onClick: () => setSelected(row), style: { cursor: "pointer" } })}
        locale={{ emptyText: "Nothing logged for this configuration yet." }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (count) => `${count} message${count === 1 ? "" : "s"}`,
          onChange: (next, size) => {
            setPage(size !== pageSize ? 1 : next);
            setPageSize(size);
          },
        }}
      />
      <LogDetail row={selected} channel={channel} onClose={() => setSelected(null)} />
    </Drawer>
  );
};

export default MessageLogsDrawer;
